import { defineStore } from 'pinia';
import { getDefaultAnalyticsDates } from '@/utils/date';
import { activityStatsApi } from '@/services/activityStatsService';

const { defaultStartDate, defaultEndDate } = getDefaultAnalyticsDates();

export const useActivityStatsStore = defineStore('activityStats', {
  state: () => ({
    searchLimit: 250,
    searchPage: 1,
    searchDirection: 'asc',
    searchField: '',
    totalRows: 0,
    detailsTableData: [],

    defaultStartDate: defaultStartDate,
    defaultEndDate: defaultEndDate,
    defaultSelectedActivityType: [
      {
        name: 'Completed',
        activity_type_id: 3,
      },
    ],

    startDate: defaultStartDate,
    endDate: defaultEndDate,
    lastUpdated: '10/31/2024',

    selectedUsers: [],
    selectedBillingType: 'Show All',
    selectedActivityTypeOnly: [
      {
        name: 'Completed',
        activity_type_id: 3,
      },
    ],
    selectedActivitySubtype: [],
    selectedPhase: [],
    selectedStatus: [],
    selectedReason: [],
    selectedPayer: [],
    selectedPlan: [],
    selectedFinancialClass: [],
    selectedBillingEntity: [],
    filtersApplied: ['Activity Type'],
    currentMonth: '',
    lastMonth: '',
    sixMonthRange: '',
    monthlyTotal: 0,
    sixMonthAvg: 0,
    monthlyVS6MonthAverageCount: {
      value: 0,
      colorTernary: 0,
      caretTernary: 0,
    },
    monthlyVS6MonthAveragePercent: {
      value: 0,
      colorTernary: 0,
      caretTernary: 0,
    },
    monthlyInsights: [],
    mostActiveDays: [],
    leaderboardUsers: [],
    topActivityCodes: [],
    isLoading: false,

    userOptions: [],
    userSearch: '',
    userSearchPage: 1,
    userSearchLimit: 10,
    userLazyLoading: false,
    userLazyLoadingActive: true,

    splitBy: { text: 'None', id: 'none' },
    breakoutChart: { categories: [], series: [], sortableSeries: [] },
    //breakout table
    breakoutHeaders: [],
    breakoutPageLimit: 250,
    breakoutPageNumber: 1,
    breakoutSearchDirection: 'asc',
    breakoutSearchField: 'some_category',
    breakoutTotalRows: 0,

    lastFetchFingerprint: null,
  }),
  getters: {
    getFiltersApplied: state => {
      return state.filtersApplied;
    },
  },
  actions: {
    resetFilters() {
      this.selectedActivityGap = [];
      this.selectedUsers = [];
      this.selectedBillingType = 'Show All';
      this.selectedBillingEntity = [];
      this.selectedActivityTypeOnly = [];
      this.selectedActivitySubtype = [];
      this.selectedPhase = [];
      this.selectedStatus = [];
      this.selectedReason = [];
      this.selectedPayer = [];
      this.selectedPlan = [];
      this.selectedFinancialClass = [];
      this.selectedBillingEntity = [];
      this.startDate = defaultStartDate;
      this.endDate = defaultEndDate;
      this.filtersApplied = [];
      this.applyFilters();
    },
    async clearFilter(filter) {
      switch (filter) {
        case 'Date Range Applied':
          this.startDate = defaultStartDate;
          this.endDate = defaultEndDate;
          break;
        case 'Activity Type':
          this.selectedActivityTypeOnly = [];
          break;
        case 'Activity Code':
          this.selectedActivitySubtype = [];
          break;
        case 'Billing Type':
          this.selectedBillingType = '';
          break;
        case 'Billing Entity':
          this.selectedBillingEntity = [];
          break;
        case 'Financial Class':
          this.selectedFinancialClass = [];
          break;
        case 'Payer':
          this.selectedPayer = [];
          break;
        case 'Plan':
          this.selectedPlan = [];
          break;
        case 'Phase':
          this.selectedPhase = [];
          break;
        case 'Status':
          this.selectedStatus = [];
          break;
        case 'Reason':
          this.selectedReason = [];
          break;
        case 'Users':
          this.selectedUsers = [];
          break;
        case 'Denial Grouping Applied':
          this.selectedDenialGroupingList = [];
          break;
        default:
          break;
      }
      this.pageNumber = 1;
      this.breakoutPageNumber = 1;
      await this.applyFilters();
    },
    async applyFilters() {
      const incomingFingerprint = JSON.stringify([
        this.startDate,
        this.endDate,
        this.selectedUsers,
        this.selectedBillingType,
        this.selectedActivityTypeOnly,
        this.selectedActivitySubtype,
        this.selectedPhase,
        this.selectedStatus,
        this.selectedReason,
        this.selectedPayer,
        this.selectedPlan,
        this.selectedFinancialClass,
        this.selectedBillingEntity,
      ]);

      if (incomingFingerprint === this.lastFetchFingerprint) {
        return;
      }

      this.filtersApplied = [];
      if (
        this.startDate !== defaultStartDate ||
        this.endDate !== defaultEndDate
      ) {
        this.filtersApplied.push('Date Range Applied');
      }

      if (this.selectedUsers && this.selectedUsers.length > 0) {
        this.filtersApplied.push('Users');
      }

      if (this.selectedBillingType && this.selectedBillingType !== 'Show All') {
        this.filtersApplied.push('Billing Type');
      }

      if (this.billingEntity && this.billingEntity.length > 0) {
        this.filtersApplied.push('Billing Entity');
      }

      if (
        this.selectedActivityTypeOnly &&
        this.selectedActivityTypeOnly.length > 0
      ) {
        this.filtersApplied.push('Activity Type');
      }

      if (
        this.selectedActivitySubtype &&
        this.selectedActivitySubtype.length > 0
      ) {
        this.filtersApplied.push('Activity Code');
      }

      if (this.selectedPhase && this.selectedPhase.length > 0) {
        this.filtersApplied.push('Phase');
      }

      if (this.selectedStatus && this.selectedStatus.length > 0) {
        this.filtersApplied.push('Status');
      }

      if (this.selectedReason && this.selectedReason.length > 0) {
        this.filtersApplied.push('Reason');
      }

      if (this.selectedPayer && this.selectedPayer.length > 0) {
        this.filtersApplied.push('Payer');
      }

      if (this.selectedPlan && this.selectedPlan.length > 0) {
        this.filtersApplied.push('Plan');
      }

      if (
        this.selectedFinancialClass &&
        this.selectedFinancialClass.length > 0
      ) {
        this.filtersApplied.push('Financial Class');
      }

      if (this.selectedBillingEntity && this.selectedBillingEntity.length > 0) {
        this.filtersApplied.push('Billing Entity');
      }

      // Reset pagination to page 1 when filters are applied
      this.breakoutPageNumber = 1;

      await Promise.all([
        this.fetchMetrics(),
        this.fetchDetails(),
        this.fetchWorkActivityBreakoutChart(),
        this.fetchWorkActivityBreakoutTable(),
        this.fetchLastUpdated(),
      ]);

      this.lastFetchFingerprint = incomingFingerprint;
    },
    async fetchMetrics() {
      this.isLoading = true;
      try {
        const payload = this._buildPayload();
        await Promise.all([
          this._fetchAverageTotalInsights(payload),
          this._fetchMonthlyInsights(payload),
          this._fetchMostActiveDays(payload),
          this._fetchLeaderboardUsers(payload),
          this._fetchTopActivityCodes(payload),
        ]);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchLastUpdated() {
      const response = await activityStatsApi.getLastUpdated();
      this.lastUpdated = response.last_updated_timestamp;
    },
    async fetchDetails() {
      try {
        this.isLoading = true;
        const response = await activityStatsApi.getDetails(
          this._buildPayloadDetails()
        );
        this.detailsTableData = response.data;
        this.totalRows = response.total_records;
      } finally {
        this.isLoading = false;
      }
    },
    async fetchWorkActivityBreakoutTable(
      splitBy = this.splitBy,
      page = this.breakoutPageNumber,
      limit = this.breakoutPageLimit,
      direction = this.breakoutSearchDirection,
      field = this.breakoutSearchField
    ) {
      if (splitBy.id === 'none') {
        return;
      }
      this.breakoutPageNumber = page;
      this.breakoutPageLimit = limit;
      this.breakoutSearchDirection = direction;
      this.breakoutSearchField = field;
      const payload = this._buildPayload();
      payload.splitBy = splitBy.id;
      let secondSortField = '';
      let thirdSortField = '';

      // Add secondary and tertiary sort fields based on the split type
      if (
        (splitBy.id === 'payerPlan' || splitBy.id === 'activityTypeCode') &&
        field === 'some_category'
      ) {
        secondSortField = 'second_category';
      } else if (splitBy.id === 'phaseStatusReason') {
        if (field === 'some_category') {
          secondSortField = 'second_category';
          thirdSortField = 'third_category';
        } else if (field === 'second_category') {
          thirdSortField = 'third_category';
        }
      }

      const pagination = {
        pageNumber: page,
        pageLimit: limit,
        sortField: field,
        secondSortField,
        thirdSortField,
        sortOrder: direction,
      };
      payload.pagination = pagination;
      const response = await activityStatsApi.getBreakoutTable(payload);
      this.breakoutChart.sortableSeries = response.series;
      this.breakoutTotalRows = response.total;
      this.updateHeaders();
    },
    async fetchWorkActivityBreakoutChart(splitBy = this.splitBy) {
      this.splitBy = splitBy;
      const payload = this._buildPayload();
      payload.splitBy = splitBy.id;
      const response = await activityStatsApi.getBreakoutChart(payload);
      this.breakoutChart.categories = response.categories;
      this.breakoutChart.series = response.series;
    },
    updateHeaders() {
      // Start with the first column (always present)
      this.breakoutHeaders = [
        {
          key: 'some_category',
          // If payerPlan, use "Payer" as the header, otherwise use the splitBy text
          value:
            this.splitBy.id === 'user'
              ? 'User'
              : this.splitBy.id === 'payerPlan'
                ? 'Payer'
                : this.splitBy.id === 'activityTypeCode'
                  ? 'Activity Type'
                  : this.splitBy.text,
          sort: true,
        },
      ];

      // Only add second_category column for payerPlan
      if (this.splitBy.id === 'payerPlan') {
        this.breakoutHeaders.push({
          key: 'second_category',
          value: 'Plan',
          sort: true,
        });
      } else if (this.splitBy.id === 'activityTypeCode') {
        this.breakoutHeaders.push({
          key: 'second_category',
          value: 'Activity Code',
          sort: true,
        });
      } else if (this.splitBy.id === 'phaseStatusReason') {
        this.breakoutHeaders.push({
          key: 'second_category',
          value: 'Status',
          sort: true,
        });
        this.breakoutHeaders.push({
          key: 'third_category',
          value: 'Reason',
          sort: true,
        });
      }

      // Add remaining columns (month categories and total)
      this.breakoutHeaders = [
        ...this.breakoutHeaders,
        ...(this.breakoutChart.categories || []).map(category => {
          const fullCategory = category.join(' ');
          return {
            key: fullCategory,
            value: fullCategory,
            sort: true,
            format: 'number',
          };
        }),
        {
          key: 'total',
          value: 'Total',
          sort: true,
          format: 'number',
        },
      ];
    },
    async _fetchAverageTotalInsights(payload) {
      const response = await activityStatsApi.getAverageTotalInsights(payload);

      this.currentMonth = response.current_total.activity_month;
      this.monthlyTotal = response.current_total.total_count;
      this.sixMonthAvg = Math.round(response.six_month_avg.avg_monthly_count);
      this.sixMonthRange = response.six_month_avg.date_range;

      this._updateComparisonMetrics(response.six_month_comparison);
    },

    _updateComparisonMetrics(data) {
      // Count comparison
      this.monthlyVS6MonthAverageCount.value = Math.abs(
        Math.round(data.difference * 10) / 10
      );
      this.monthlyVS6MonthAverageCount.colorTernary =
        data.difference > 0 ? 2 : 1;
      this.monthlyVS6MonthAverageCount.caretTernary =
        data.difference > 0 ? 1 : 2;

      // Percentage comparison
      this.monthlyVS6MonthAveragePercent.value = Math.abs(
        Math.round(data.percent_difference * 10) / 10
      );
      this.monthlyVS6MonthAveragePercent.colorTernary =
        data.percent_difference > 0 ? 2 : 1;
      this.monthlyVS6MonthAveragePercent.caretTernary =
        data.percent_difference > 0 ? 1 : 2;

      this.lastMonth = data.last_month;
    },

    async _fetchMonthlyInsights(payload) {
      const response = await activityStatsApi.getMonthlyInsights(payload);

      this.monthlyInsights = response.map(item => this._formatMonthlyInsight(item));
    },

    _formatMonthlyInsight(item) {
      return {
        key: item.type,
        value: item.total_count,
        textValue: item.activity_month,
        labelText: `${
          item.type.charAt(0).toUpperCase() + item.type.slice(1)
        } Month:`,
        colorTernary:
          item.type === 'current' ? 0 : item.type === 'highest' ? 2 : 1,
        showWarning: item.type === 'current' && this.filtersApplied.includes('Date Range Applied'),
      };
    },
    async _fetchMostActiveDays(payload) {
      const response = await activityStatsApi.getMostActiveDays(payload);

      this.mostActiveDays = response.map(item => ({
        value: item.total_count,
        textValue: item.weekday,
      }));
    },
    async _fetchLeaderboardUsers(payload) {
      const response = await activityStatsApi.getLeaderboard(payload);

      this.leaderboardUsers = response.map(item => ({
        user: `${item.first_name} ${item.last_name}`,
        user_id: item.user_id,
        total: item.total_count,
      }));
    },
    async _fetchTopActivityCodes(payload) {
      const response = await activityStatsApi.getTopActivityCodes(payload);

      this.topActivityCodes = response.map(item => ({
        activity_subtype_id: item.activity_subtype_id,
        activity_subtype_name: item.activity_subtype_name,
        total: item.total_count,
      }));
    },
    condenseSeriesList(seriesList) {
      // Map seriesList to get totals and data
      const pleaseWork = seriesList.map(series => {
        const total = series.data.reduce((sum, a) => sum + a, 0);
        return {
          total,
          name: series.secondName
            ? `${series.name}/${series.secondName}`
            : series.name,
          data: series.data, // Keep the data as well
        };
      });

      pleaseWork.sort((a, b) => b.total - a.total);

      // Get top 5 highest totals
      const top5 = pleaseWork.slice(0, 5);

      const others = pleaseWork.slice(5).reduce(
        (acc, series) => {
          acc.total += series.total;
          series.data.forEach((value, index) => {
            acc.data[index] = (acc.data[index] || 0) + value;
          });
          return acc;
        },
        { name: 'Other', total: 0, data: [] }
      );

      return [...top5, others];
    },

    createTotalSeries(seriesList) {
      if (seriesList.length === 0) return { name: 'Total', data: [] };

      const maxLength = Math.max(
        ...seriesList.map(series => series.data.length)
      );

      const totalData = new Array(maxLength).fill(0);

      seriesList.forEach(series => {
        series.data.forEach((value, index) => {
          totalData[index] += value;
        });
      });

      return { name: 'Total', data: totalData };
    },
    getOrCreateSeries(seriesList, seriesName, secondaryName, categoryLength) {
      let seriesItem = seriesList.find(s => s.name === seriesName);
      if (secondaryName) {
        seriesItem = seriesList.find(
          s => s.name === seriesName && s.secondName === secondaryName
        );
      }
      if (!seriesItem) {
        if (secondaryName) {
          seriesItem = {
            name: seriesName,
            secondName: secondaryName,
            data: new Array(categoryLength).fill(0),
          };
        } else {
          seriesItem = {
            name: seriesName,
            data: new Array(categoryLength).fill(0),
          };
        }
        seriesList.push(seriesItem);
      }

      return seriesItem;
    },
    get_pagination() {
      return {
        sortField: this.searchField,
        sortOrder: this.searchDirection,
      };
    },
    // Helper methods
    _buildPayload() {
      return {
        filter: {
          startDate: this.startDate,
          endDate: this.endDate,
          billingType:
            this.selectedBillingType === 'Show All'
              ? ''
              : this.selectedBillingType,
          auxoUsers: this.selectedUsers.length
            ? this.selectedUsers.map(user => user.id)
            : [],
          activityTypeOnly: this.selectedActivityTypeOnly.length
            ? this.selectedActivityTypeOnly.map(item => item.activity_type_id)
            : [],
          activitySubtype: this.selectedActivitySubtype.length
            ? this.selectedActivitySubtype.map(item => item.activity_subtype_id)
            : [],
          billingEntity: this.selectedBillingEntity.length
            ? this.selectedBillingEntity.map(entry => entry.value)
            : [],
          financialClass: this.selectedFinancialClass.length
            ? this.selectedFinancialClass.map(entry => entry.value)
            : [],
          payer: this.selectedPayer.length
            ? this.selectedPayer.map(entry => entry.value)
            : [],
          plan: this.selectedPlan.length
            ? this.selectedPlan.map(entry => entry.plan_name || entry.value)
            : [],
          phase: this.selectedPhase.length
            ? this.selectedPhase.map(item => item.phase)
            : [],
          status: this.selectedStatus.length
            ? this.selectedStatus.map(item => item.status)
            : [],
          reason: this.selectedReason.length
            ? this.selectedReason.map(item => item.reason)
            : [],
        },
      };
    },
    _buildPayloadDetails() {
      return {
        filter: {
          startDate: this.startDate,
          endDate: this.endDate,
          billingType:
            this.selectedBillingType === 'Show All'
              ? ''
              : this.selectedBillingType,
          auxoUsers: this.selectedUsers.length
            ? this.selectedUsers.map(user => user.id)
            : [],
          activityTypeOnly: this.selectedActivityTypeOnly.length
            ? this.selectedActivityTypeOnly.map(item => item.activity_type_id)
            : [],
          activitySubtype: this.selectedActivitySubtype.length
            ? this.selectedActivitySubtype.map(item => item.activity_subtype_id)
            : [],
          billingEntity: this.selectedBillingEntity.length
            ? this.selectedBillingEntity.map(entry => entry.value)
            : [],
          financialClass: this.selectedFinancialClass.length
            ? this.selectedFinancialClass.map(entry => entry.value)
            : [],
          payer: this.selectedPayer.length
            ? this.selectedPayer.map(entry => entry.value)
            : [],
          plan: this.selectedPlan.length
            ? this.selectedPlan.map(entry => entry.plan_name || entry.value)
            : [],
          phase: this.selectedPhase.length
            ? this.selectedPhase.map(item => item.phase)
            : [],
          status: this.selectedStatus.length
            ? this.selectedStatus.map(item => item.status)
            : [],
          reason: this.selectedReason.length
            ? this.selectedReason.map(item => item.reason)
            : [],
        },
        pagination: {
          pageNumber: this.searchPage,
          pageLimit: this.searchLimit,
          sortField: this.searchField,
          sortOrder: this.searchDirection,
        },
      };
    },
  },
});
